import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { api, extractErrorMessage } from "../lib/api";
import { useAuth, hasAccess } from "../context/AuthContext";
import { ChevronLeft, GitBranch, Plus, Trash2, X, Bug, Sparkles, ShieldAlert, Wrench } from "lucide-react";

const TYPE_META = {
  feature: { label: "Feature", icon: Sparkles, dot: "bg-envicom-green", chip: "text-envicom-green border-envicom-green/40 bg-envicom-green/10" },
  bugfix: { label: "Bug fix", icon: Bug, dot: "bg-envicom-warning", chip: "text-envicom-warning border-envicom-warning/40 bg-envicom-warning/10" },
  improvement: { label: "Improvement", icon: Wrench, dot: "bg-envicom-blue", chip: "text-envicom-blue border-envicom-blue/40 bg-envicom-blue/10" },
  security: { label: "Security", icon: ShieldAlert, dot: "bg-envicom-error", chip: "text-envicom-error border-envicom-error/40 bg-envicom-error/10" },
};

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function VersionControlApp({
  apiPath = "/versions",
  appId = "version_control",
  title = "Version Control",
  subtitle = "Track every release and the changes that went into it.",
  productBadge = null,
} = {}) {
  const { user } = useAuth();
  const canWrite = hasAccess(user, appId, true);

  const [releases, setReleases] = useState([]);
  const [error, setError] = useState("");
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [releaseDraft, setReleaseDraft] = useState(blankRelease());

  const fetchAll = async () => {
    try {
      const { data } = await api.get(apiPath);
      setReleases(data);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  useEffect(() => { fetchAll(); }, [apiPath]);

  const submitRelease = async (e) => {
    e.preventDefault();
    setError("");
    const cleanEntries = releaseDraft.entries
      .map((en) => ({ type: en.type, description: (en.description || "").trim() }))
      .filter((en) => en.description.length > 0);
    if (cleanEntries.length === 0) {
      setError("Add at least one entry with a description.");
      return;
    }
    try {
      const payload = {
        version: releaseDraft.version,
        release_date: new Date(releaseDraft.release_date).toISOString(),
        entries: cleanEntries,
        is_released: releaseDraft.is_released,
        build: releaseDraft.build?.trim() || null,
        dev_tool: releaseDraft.dev_tool?.trim() || null,
      };
      if (releaseDraft.id) {
        await api.patch(`${apiPath}/${releaseDraft.id}`, payload);
      } else {
        await api.post(apiPath, payload);
      }
      setShowReleaseModal(false);
      setReleaseDraft(blankRelease());
      fetchAll();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const deleteRelease = async (id) => {
    if (!window.confirm("Delete this release?")) return;
    try {
      await api.delete(`${apiPath}/${id}`);
      fetchAll();
    } catch (err) { setError(extractErrorMessage(err)); }
  };

  const addEntry = () => {
    setReleaseDraft((d) => ({ ...d, entries: [...d.entries, { type: "feature", description: "" }] }));
  };
  const removeEntry = (idx) => {
    setReleaseDraft((d) => ({ ...d, entries: d.entries.filter((_, i) => i !== idx) }));
  };
  const updateEntry = (idx, patch) => {
    setReleaseDraft((d) => ({
      ...d,
      entries: d.entries.map((en, i) => (i === idx ? { ...en, ...patch } : en)),
    }));
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10 relative z-10">
        <Link to="/" data-testid="vc-back-link" className="inline-flex items-center gap-1 text-envicom-muted hover:text-envicom-text font-mono text-sm">
          <ChevronLeft className="w-4 h-4" /> Back to tools
        </Link>

        <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded flex items-center justify-center bg-envicom-blue/15 text-envicom-blue border border-envicom-blue/30">
              <GitBranch className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-mono text-2xl md:text-3xl font-bold tracking-tight">{title}</h1>
                {productBadge && (
                  <span
                    data-testid="vc-product-badge"
                    className={`text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 rounded border ${productBadge.className}`}
                  >
                    {productBadge.label}
                  </span>
                )}
              </div>
              <p className="text-envicom-muted text-sm">{subtitle}</p>
            </div>
          </div>
          {canWrite && (
            <button
              type="button"
              onClick={() => { setReleaseDraft(blankRelease()); setShowReleaseModal(true); }}
              data-testid="vc-new-release-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded bg-envicom-accent hover:bg-sky-400 text-black font-semibold font-mono text-sm"
            >
              <Plus className="w-4 h-4" /> New release
            </button>
          )}
        </div>

        {error && (
          <div data-testid="vc-error" className="mt-4 px-3 py-2 rounded bg-envicom-error/10 border border-envicom-error/30 text-envicom-error text-sm">
            {error}
          </div>
        )}

        <section className="mt-8" data-testid="releases-section">
          {releases.length === 0 ? (
            <div className="p-12 text-center border border-dashed border-envicom-border rounded text-envicom-muted">
              No releases tracked yet. {canWrite && <button onClick={() => { setReleaseDraft(blankRelease()); setShowReleaseModal(true); }} className="text-envicom-accent hover:underline">Create the first one →</button>}
            </div>
          ) : (
            <ol className="relative border-l border-envicom-border ml-4 space-y-8">
              {releases.map((r) => {
                const firstType = r.entries?.[0]?.type || "improvement";
                const dotColor = TYPE_META[firstType]?.dot || "bg-envicom-blue";
                return (
                  <li key={r.id} data-testid={`release-item-${r.id}`} className="pl-8 relative animate-fade-up">
                    <span className={`absolute -left-[7px] top-2 w-3.5 h-3.5 rounded-full border-2 border-envicom-bg ${dotColor}`} />
                    <div className="bg-envicom-surface border border-envicom-border rounded-md p-5">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-mono font-bold text-envicom-text text-base">v{r.version}</span>
                          {!r.is_released && (
                            <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-envicom-border text-envicom-muted rounded">Draft</span>
                          )}
                          <span className="text-envicom-muted text-xs font-mono">{formatDate(r.release_date)}</span>
                          {r.build && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border border-envicom-border text-envicom-muted">
                              <span className="text-envicom-muted/70">Build</span>
                              <span className="text-envicom-text normal-case tracking-normal">{r.build}</span>
                            </span>
                          )}
                          {r.dev_tool && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border border-envicom-border text-envicom-muted">
                              <span className="text-envicom-muted/70">Dev Tool</span>
                              <span className="text-envicom-text normal-case tracking-normal">{r.dev_tool}</span>
                            </span>
                          )}
                        </div>
                        {canWrite && (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setReleaseDraft({
                                  id: r.id,
                                  version: r.version,
                                  release_date: r.release_date.slice(0, 10),
                                  entries: r.entries?.length ? r.entries : [{ type: "feature", description: "" }],
                                  is_released: r.is_released,
                                  build: r.build || "",
                                  dev_tool: r.dev_tool || "",
                                });
                                setShowReleaseModal(true);
                              }}
                              className="text-envicom-muted hover:text-envicom-text text-xs font-mono"
                            >
                              Edit
                            </button>
                            <button type="button" onClick={() => deleteRelease(r.id)} className="text-envicom-muted hover:text-envicom-error" data-testid={`release-delete-${r.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                      <ul className="mt-4 space-y-2.5">
                        {(r.entries || []).map((en, i) => {
                          const meta = TYPE_META[en.type] || TYPE_META.improvement;
                          const Icon = meta.icon;
                          return (
                            <li key={i} className="flex items-start gap-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-mono uppercase tracking-wider whitespace-nowrap mt-0.5 ${meta.chip}`}>
                                <Icon className="w-3 h-3" /> {meta.label}
                              </span>
                              <span className="text-sm text-envicom-text whitespace-pre-wrap leading-relaxed">{en.description}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      </main>

      {showReleaseModal && (
        <Modal onClose={() => setShowReleaseModal(false)} title={releaseDraft.id ? "Edit release" : "New release"}>
          <form onSubmit={submitRelease} className="space-y-4" data-testid="release-form">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Version">
                <input required value={releaseDraft.version} onChange={(e) => setReleaseDraft({ ...releaseDraft, version: e.target.value })} placeholder="2.4.1" data-testid="release-version-input" className={inputCls} />
              </Field>
              <Field label="Release date">
                <input type="date" required value={releaseDraft.release_date} onChange={(e) => setReleaseDraft({ ...releaseDraft, release_date: e.target.value })} className={inputCls} data-testid="release-date-input" />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Build version (optional)">
                <input
                  value={releaseDraft.build}
                  onChange={(e) => setReleaseDraft({ ...releaseDraft, build: e.target.value })}
                  placeholder="e.g. Build 17234"
                  data-testid="release-build-input"
                  className={inputCls}
                />
              </Field>
              <Field label="Dev tool version (optional)">
                <input
                  value={releaseDraft.dev_tool}
                  onChange={(e) => setReleaseDraft({ ...releaseDraft, dev_tool: e.target.value })}
                  placeholder="e.g. JMobile Studio 4.5 SP1"
                  data-testid="release-devtool-input"
                  className={inputCls}
                />
              </Field>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-envicom-muted text-xs uppercase tracking-wider font-mono">Entries</span>
                <button type="button" onClick={addEntry} data-testid="release-add-entry-btn" className="inline-flex items-center gap-1 text-envicom-accent text-xs font-mono hover:underline">
                  <Plus className="w-3 h-3" /> Add entry
                </button>
              </div>
              <div className="space-y-2">
                {releaseDraft.entries.map((en, idx) => (
                  <div key={idx} className="flex items-start gap-2 bg-envicom-input border border-envicom-border rounded p-2" data-testid={`release-entry-row-${idx}`}>
                    <select
                      value={en.type}
                      onChange={(e) => updateEntry(idx, { type: e.target.value })}
                      data-testid={`release-entry-type-${idx}`}
                      className="bg-envicom-bg border border-envicom-border rounded px-2 py-2 text-envicom-text text-xs font-mono focus:outline-none focus:border-envicom-accent shrink-0"
                    >
                      {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                    <input
                      type="text"
                      value={en.description}
                      onChange={(e) => updateEntry(idx, { description: e.target.value })}
                      placeholder="Describe the change…"
                      data-testid={`release-entry-desc-${idx}`}
                      className="flex-1 bg-envicom-bg border border-envicom-border rounded px-3 py-2 text-envicom-text focus:outline-none focus:border-envicom-accent text-sm"
                    />
                    {releaseDraft.entries.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEntry(idx)}
                        data-testid={`release-entry-remove-${idx}`}
                        className="text-envicom-muted hover:text-envicom-error p-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-envicom-muted">
              <input type="checkbox" checked={releaseDraft.is_released} onChange={(e) => setReleaseDraft({ ...releaseDraft, is_released: e.target.checked })} data-testid="release-released-checkbox" />
              Released (uncheck for draft)
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowReleaseModal(false)} className="px-4 py-2 rounded border border-envicom-border text-envicom-muted text-sm font-mono">Cancel</button>
              <button type="submit" data-testid="release-submit-btn" className="px-4 py-2 rounded bg-envicom-accent hover:bg-sky-400 text-black font-semibold font-mono text-sm">
                {releaseDraft.id ? "Save" : "Create"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

const inputCls = "w-full bg-envicom-input border border-envicom-border rounded px-3 py-2 text-envicom-text focus:outline-none focus:border-envicom-accent text-sm";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-envicom-muted text-xs uppercase tracking-wider font-mono">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fade-up" onClick={onClose}>
      <div className="bg-envicom-surface border border-envicom-border rounded-md w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-envicom-border">
          <h2 className="font-mono font-bold text-envicom-text">{title}</h2>
          <button onClick={onClose} className="text-envicom-muted hover:text-envicom-text" data-testid="modal-close-btn"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function blankRelease() {
  const today = new Date().toISOString().slice(0, 10);
  return {
    id: null,
    version: "",
    release_date: today,
    entries: [{ type: "feature", description: "" }],
    is_released: true,
    build: "",
    dev_tool: "",
  };
}
